import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import gsap from 'gsap';
import { useSound } from '../hooks/useSound';
import { Confetti } from '../lib/animations';
import { GameHeader, ScoreBadge, FloatingParticles } from '../lib/GameVisuals';
import { ArrowDownUp, Zap, CheckCircle2, Train } from 'lucide-react';

/* ─── Sort Type Metadata ─── */
const SORT_TYPE_INFO = {
  vocabulary_category: { label: 'Vocabulary', color: '#4C6EF5', emoji: 'ABC' },
  gender: { label: 'Gender', color: '#F06595', emoji: 'M/F' },
  tense: { label: 'Tenses', color: '#0CA678', emoji: 'T' },
  tense_sorting: { label: 'Tenses', color: '#0CA678', emoji: 'T' },
  comparison_type: { label: 'Grammar', color: '#7950F2', emoji: 'G' },
  conjunction_type: { label: 'Grammar', color: '#7950F2', emoji: 'G' },
  verb_type: { label: 'Grammar', color: '#7950F2', emoji: 'G' },
  auxiliary_selection: { label: 'Grammar', color: '#7950F2', emoji: 'G' },
  grammar_category: { label: 'Grammar', color: '#7950F2', emoji: 'G' },
  opinion_vs_fact: { label: 'Grammar', color: '#7950F2', emoji: 'G' },
  grammar: { label: 'Grammar', color: '#7950F2', emoji: 'G' },
  agreement_form: { label: 'Grammar', color: '#7950F2', emoji: 'G' },
  agreement_number: { label: 'Grammar', color: '#7950F2', emoji: 'G' },
  auxiliary_in_context: { label: 'Grammar', color: '#7950F2', emoji: 'G' },
};

function getSortTypeInfo(sortType) {
  return SORT_TYPE_INFO[sortType] || { label: 'Sort', color: '#4C6EF5', emoji: '?' };
}

/* ─── Category Bin Colors ─── */
const CAT_COLORS = [
  { bg: '#E03131', border: '#C92A2A', light: '#FFF5F5', glow: 'rgba(224,49,49,0.3)' },
  { bg: '#1971C2', border: '#1864AB', light: '#E7F5FF', glow: 'rgba(25,113,194,0.3)' },
  { bg: '#F59F00', border: '#E67700', light: '#FFF9DB', glow: 'rgba(245,159,0,0.3)' },
  { bg: '#2F9E44', border: '#2B8A3E', light: '#EBFBEE', glow: 'rgba(47,158,68,0.3)' },
];

/* ═══════════════════════════════════════════
   TUTORIAL SCREEN
   ═══════════════════════════════════════════ */
function TutorialScreen({ items, onStart }) {
  const containerRef = useRef(null);
  const firstSet = items[0];
  const stInfo = firstSet ? getSortTypeInfo(firstSet.sort_type) : { label: 'Sort', color: '#4C6EF5' };

  useEffect(() => {
    if (!containerRef.current) return;
    const cards = containerRef.current.querySelectorAll('.tut-card');
    gsap.fromTo(cards, { y: 40, opacity: 0, scale: 0.9 }, { y: 0, opacity: 1, scale: 1, duration: 0.5, stagger: 0.15, ease: 'back.out(1.5)' });
  }, []);

  // Get example from first set
  const exampleCategories = firstSet?.categories || [];
  const exampleWords = (firstSet?.items || []).slice(0, 4);

  return (
    <div ref={containerRef} data-testid="tri-tutorial" style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: '2rem',
      background: 'linear-gradient(180deg, #F0F4F8 0%, #E8EDF4 100%)',
      padding: '2rem',
    }}>
      <FloatingParticles type="star" count={6} />

      <div style={{ textAlign: 'center', position: 'relative', zIndex: 5 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <ArrowDownUp size={40} color="var(--primary)" />
          <h1 style={{ fontFamily: 'Fredoka', fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
            TRI EXPRESS
          </h1>
        </div>
        <p style={{ fontFamily: 'Nunito', fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)', color: 'var(--text-secondary)', fontWeight: 600, margin: 0 }}>
          Sort each word into the correct category!
        </p>
      </div>

      {/* Instruction cards */}
      <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', justifyContent: 'center', maxWidth: 900, position: 'relative', zIndex: 5 }}>
        <div className="tut-card" style={tutCardStyle('#4C6EF5')}>
          <div style={tutIconStyle('#4C6EF5')}>
            <ArrowDownUp size={28} color="white" />
          </div>
          <div style={tutTitleStyle}>Sort!</div>
          <div style={tutDescStyle}>A word appears at the top. Tap the category where it belongs.</div>
        </div>

        <div className="tut-card" style={tutCardStyle('#E03131')}>
          <div style={tutIconStyle('#E03131')}>
            <CheckCircle2 size={28} color="white" />
          </div>
          <div style={tutTitleStyle}>Categories</div>
          <div style={tutDescStyle}>Each set has 2-4 category bins. Read the word carefully and choose the right one!</div>
        </div>

        <div className="tut-card" style={tutCardStyle('#F59F00')}>
          <div style={tutIconStyle('#F59F00')}>
            <Zap size={28} color="white" />
          </div>
          <div style={tutTitleStyle}>Streak!</div>
          <div style={tutDescStyle}>Sort correctly in a row to build a streak. How far can you go?</div>
        </div>
      </div>

      {/* Example from actual data */}
      {firstSet && (
        <div style={{
          background: 'rgba(255,255,255,0.95)', borderRadius: '1.25rem', padding: '1rem 1.5rem',
          border: '2px solid rgba(0,0,0,0.06)', maxWidth: 700, width: '100%',
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)', position: 'relative', zIndex: 5,
        }}>
          <div style={{ fontFamily: 'Fredoka', fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '0.5rem', textAlign: 'center' }}>
            Example: <span style={{
              display: 'inline-block', padding: '0.1rem 0.5rem', borderRadius: '0.3rem',
              fontSize: '0.8rem', fontWeight: 700, background: `${stInfo.color}15`, color: stInfo.color,
            }}>{stInfo.label}</span>
          </div>
          <div style={{ fontFamily: 'Fredoka', fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', textAlign: 'center', marginBottom: '0.75rem' }}>
            {firstSet.title || firstSet.title_en}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
            {exampleCategories.map((cat, idx) => (
              <div key={idx} style={{
                padding: '0.4rem 1rem', borderRadius: '0.75rem',
                background: CAT_COLORS[idx % CAT_COLORS.length].bg, color: 'white',
                fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 700,
              }}>{cat.label}</div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            {exampleWords.map((w, i) => {
              const catColor = CAT_COLORS[w.category % CAT_COLORS.length];
              return (
                <span key={i} style={{
                  padding: '0.25rem 0.75rem', borderRadius: '0.5rem',
                  border: `2px solid ${catColor.bg}`, background: catColor.light,
                  fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 600, color: catColor.bg,
                }}>{w.display || w.text}</span>
              );
            })}
          </div>
        </div>
      )}

      <button data-testid="tri-start-btn" onClick={onStart} style={{
        minHeight: 64, padding: '0 3rem', fontSize: '1.75rem', fontWeight: 700,
        fontFamily: 'Fredoka', background: 'var(--primary)', color: 'white',
        border: 'none', borderBottom: '6px solid #3B5BDB', borderRadius: '1.25rem',
        cursor: 'pointer', touchAction: 'manipulation',
        boxShadow: '0 4px 16px rgba(76,110,245,0.3)',
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        position: 'relative', zIndex: 5,
        transition: 'transform 0.1s',
      }}
        onMouseDown={e => e.currentTarget.style.transform = 'translateY(4px)'}
        onMouseUp={e => e.currentTarget.style.transform = ''}
      >
        <Train size={28} /> Let's Go!
      </button>
    </div>
  );
}

const tutCardStyle = (color) => ({
  background: 'rgba(255,255,255,0.95)', borderRadius: '1.25rem', padding: '1.25rem 1rem',
  width: 220, textAlign: 'center',
  border: `2px solid ${color}20`,
  boxShadow: `0 4px 16px ${color}15`,
});
const tutIconStyle = (color) => ({
  width: 52, height: 52, borderRadius: '50%', background: color,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  margin: '0 auto 0.5rem',
});
const tutTitleStyle = { fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.25rem' };
const tutDescStyle = { fontFamily: 'Nunito', fontSize: '0.95rem', color: 'var(--text-secondary)', lineHeight: 1.3, fontWeight: 600 };

/* ═══════════════════════════════════════════
   SET TRANSITION SCREEN
   ═══════════════════════════════════════════ */
function SetTransition({ setIdx, maxSets, currentSet, onDone }) {
  const [countdown, setCountdown] = useState(3);
  const containerRef = useRef(null);
  const stInfo = getSortTypeInfo(currentSet?.sort_type);

  useEffect(() => {
    if (containerRef.current) {
      gsap.fromTo(containerRef.current, { scale: 0.8, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.4, ease: 'back.out(1.5)' });
    }
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(timer); setTimeout(onDone, 200); return 0; }
        return prev - 1;
      });
    }, 800);
    return () => clearInterval(timer);
  }, [onDone]);

  const categories = currentSet?.categories || [];

  return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #F0F4F8 0%, #E8EDF4 100%)',
    }}>
      <div ref={containerRef} style={{ textAlign: 'center', position: 'relative', zIndex: 5 }}>
        <div style={{
          fontFamily: 'Fredoka', fontSize: 'clamp(2.5rem, 6vw, 4rem)', fontWeight: 700,
          color: 'var(--primary)', marginBottom: '0.5rem',
        }}>
          SET {setIdx + 1} OF {maxSets}
        </div>

        <div style={{
          display: 'inline-block', padding: '0.2rem 0.75rem', borderRadius: '0.5rem',
          background: `${stInfo.color}15`, color: stInfo.color,
          fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem',
        }}>{stInfo.label}</div>

        <div style={{
          fontFamily: 'Fredoka', fontSize: 'clamp(1.3rem, 3vw, 2rem)', fontWeight: 700,
          color: 'var(--text-primary)', marginBottom: '1rem', maxWidth: 600,
        }}>
          {currentSet?.title || currentSet?.title_en || 'Sort!'}
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          {categories.map((cat, idx) => (
            <div key={idx} style={{
              padding: '0.5rem 1.25rem', borderRadius: '1rem',
              background: CAT_COLORS[idx % CAT_COLORS.length].bg, color: 'white',
              fontFamily: 'Fredoka', fontSize: 'clamp(1rem, 2vw, 1.4rem)', fontWeight: 700,
              boxShadow: `0 3px 12px ${CAT_COLORS[idx % CAT_COLORS.length].glow}`,
            }}>{cat.label}</div>
          ))}
        </div>

        <div style={{
          fontFamily: 'Fredoka', fontSize: '1.5rem', fontWeight: 700,
          color: 'var(--text-muted)',
        }}>
          {currentSet?.items?.length || '?'} words to sort
        </div>

        {countdown > 0 && (
          <div style={{
            marginTop: '1rem', fontFamily: 'Fredoka', fontSize: '3rem', fontWeight: 700,
            color: 'var(--primary)', opacity: 0.6,
          }}>{countdown}</div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN GAME COMPONENT
   ═══════════════════════════════════════════ */
export function TriExpress({ items, settings, onGameEnd }) {
  const { playSound, playGameMusic, stopMusic } = useSound();
  useEffect(() => { playGameMusic('tri_express'); return () => stopMusic(); }, [playGameMusic, stopMusic]);
  const triItems = useMemo(() => items.filter(i => i.categories && i.items), [items]);
  const maxSets = Math.min(settings.rounds, triItems.length);

  // Game state
  const [phase, setPhase] = useState('tutorial'); // tutorial | transition | playing
  const [setIdx, setSetIdx] = useState(0);
  const [wordIdx, setWordIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [errors, setErrors] = useState(0);
  const [totalDone, setTotalDone] = useState(0);
  const [feedback, setFeedback] = useState(null); // null | 'correct' | 'wrong'
  const [correctCategory, setCorrectCategory] = useState(null);
  const [showConfetti, setShowConfetti] = useState(0);
  const [sortedWords, setSortedWords] = useState({}); // catIdx -> [word strings]
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);

  // Refs
  const scoreRef = useRef(null);
  const streakRef = useRef(null);
  const wordRef = useRef(null);
  const endedRef = useRef(false);
  const reviewSetsRef = useRef([]);
  const currentSetReviewRef = useRef([]);

  const currentSet = triItems[setIdx];
  const currentWords = currentSet?.items || [];
  const currentWord = currentWords[wordIdx];
  const categories = currentSet?.categories || [];
  const stInfo = currentSet ? getSortTypeInfo(currentSet.sort_type) : { label: 'Sort', color: '#4C6EF5' };

  // Animate word entrance
  useEffect(() => {
    if (phase !== 'playing' || !wordRef.current) return;
    gsap.fromTo(wordRef.current,
      { y: -50, opacity: 0, scale: 0.85, rotation: -3 },
      { y: 0, opacity: 1, scale: 1, rotation: 0, duration: 0.4, ease: 'back.out(2)' }
    );
  }, [wordIdx, setIdx, phase]);

  // Reset sorted words on new set
  useEffect(() => { setSortedWords({}); }, [setIdx]);

  const handleTutorialDone = useCallback(() => {
    setPhase('transition');
  }, []);

  const handleTransitionDone = useCallback(() => {
    setPhase('playing');
  }, []);

  const advanceSet = useCallback(() => {
    // Save current set review
    if (currentSetReviewRef.current.length > 0 && currentSet) {
      const catLabels = (currentSet.categories || []).map(c => ({ label: c.label, color: CAT_COLORS[c.index % CAT_COLORS.length]?.bg }));
      reviewSetsRef.current.push({
        setLabel: `Set ${setIdx + 1}`,
        setTitle: currentSet.title || currentSet.title_en || '',
        categories: catLabels,
        words: [...currentSetReviewRef.current],
      });
      currentSetReviewRef.current = [];
    }
    const nextSet = setIdx + 1;
    if (nextSet >= maxSets) {
      endGame();
    } else {
      setSetIdx(nextSet);
      setWordIdx(0);
      setPhase('transition');
    }
  }, [setIdx, maxSets, currentSet]);

  const handleSort = useCallback((catIdx, btnEl) => {
    if (feedback || !currentWord || phase !== 'playing') return;
    const correct = currentWord.category === catIdx;
    setTotalDone(d => d + 1);

    if (correct) {
      playSound('correct');
      setScore(s => s + 1);
      setFeedback('correct');
      setStreak(s => {
        const newStreak = s + 1;
        setBestStreak(b => Math.max(b, newStreak));
        return newStreak;
      });
      // Track for review
      const catLabel = categories.find(c => c.index === catIdx)?.label || '?';
      currentSetReviewRef.current.push({
        text: currentWord.display || currentWord.text, correctCategory: catLabel, wasCorrect: true,
      });
      // Add word to the bin
      const wordLabel = currentWord.display || currentWord.text;
      setSortedWords(prev => ({
        ...prev,
        [catIdx]: [...(prev[catIdx] || []), wordLabel],
      }));

      // Animations
      if (scoreRef.current) gsap.fromTo(scoreRef.current, { scale: 1.4 }, { scale: 1, duration: 0.4, ease: 'elastic.out(1,0.5)' });
      if (wordRef.current) gsap.to(wordRef.current, { y: 80, opacity: 0, scale: 0.5, duration: 0.3, ease: 'power2.in' });
      if (btnEl) {
        gsap.fromTo(btnEl, { scale: 1.04 }, { scale: 1, duration: 0.35, ease: 'elastic.out(1,0.5)' });
      }
      if (streakRef.current && streak >= 2) {
        gsap.fromTo(streakRef.current, { scale: 1.3 }, { scale: 1, duration: 0.4, ease: 'elastic.out(1,0.5)' });
      }
    } else {
      playSound('wrong');
      setErrors(e => e + 1);
      setFeedback('wrong');
      setStreak(0);
      setCorrectCategory(currentWord.category);
      // Track for review
      const correctCatLabel = categories.find(c => c.index === currentWord.category)?.label || '?';
      currentSetReviewRef.current.push({
        text: currentWord.display || currentWord.text, correctCategory: correctCatLabel, wasCorrect: false,
      });
      if (wordRef.current) gsap.fromTo(wordRef.current, { x: 0 }, { x: 12, duration: 0.05, repeat: 5, yoyo: true });
    }

    setTimeout(() => {
      setFeedback(null);
      setCorrectCategory(null);
      const next = wordIdx + 1;
      if (next >= currentWords.length) {
        advanceSet();
      } else {
        setWordIdx(next);
      }
    }, correct ? 400 : 700);
  }, [feedback, currentWord, wordIdx, currentWords.length, phase, streak, playSound, advanceSet]);

  const endGame = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    // Save last set if not saved
    if (currentSetReviewRef.current.length > 0 && triItems[setIdx]) {
      const cs = triItems[setIdx];
      const catLabels = (cs.categories || []).map(c => ({ label: c.label, color: CAT_COLORS[c.index % CAT_COLORS.length]?.bg }));
      reviewSetsRef.current.push({
        setLabel: `Set ${setIdx + 1}`, setTitle: cs.title || cs.title_en || '',
        categories: catLabels, words: [...currentSetReviewRef.current],
      });
    }
    setShowConfetti(c => c + 1);
    playSound('win');
    setTimeout(() => onGameEnd({
      team1Score: score, team2Score: 0,
      totalQuestions: totalDone,
      accuracy: totalDone > 0 ? (score / totalDone) * 100 : 0,
      bestStreak: bestStreak,
      questionReview: reviewSetsRef.current, reviewType: 'category',
    }), 1500);
  }, [score, totalDone, bestStreak, onGameEnd, playSound, setIdx, triItems]);

  // Skip-to-end: parent fires this when teacher hits "Skip" in the game menu
  useEffect(() => {
    const onForceEnd = () => endGame();
    window.addEventListener('frenchboard:end-game-now', onForceEnd);
    return () => window.removeEventListener('frenchboard:end-game-now', onForceEnd);
  }, [endGame]);

  // ─── RENDER ───

  if (phase === 'tutorial') {
    return <TutorialScreen items={triItems} onStart={handleTutorialDone} />;
  }

  if (phase === 'transition' && currentSet) {
    return <SetTransition setIdx={setIdx} maxSets={maxSets} currentSet={currentSet} onDone={handleTransitionDone} />;
  }

  if (!currentSet || !currentWord) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', color: 'var(--text-muted)', fontSize: '1.5rem', fontFamily: 'Fredoka' }}>
        No sorting content available
      </div>
    );
  }

  const catCount = categories.length;
  const sortedCount = (idx) => (sortedWords[idx] || []).length;

  return (
    <div data-testid="tri-express-game" style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      background: 'linear-gradient(180deg, #F0F4F8 0%, #E8EDF4 100%)',
    }}>
      <Confetti trigger={showConfetti} />

      {/* ─── Header (shared platform component) ─── */}
      <GameHeader title="TRI EXPRESS" icon={<ArrowDownUp size={24} color="var(--primary)" />}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
          padding: '0.2rem 0.6rem', borderRadius: '0.5rem',
          background: `${stInfo.color}12`, fontSize: '0.8rem', fontWeight: 700,
          fontFamily: 'Fredoka', color: stInfo.color,
        }}>{stInfo.label}</div>
        <ScoreBadge label="SORTED" value={score} color="var(--success)" scoreRef={scoreRef} />
        <ScoreBadge label="ERRORS" value={errors} color="var(--error)" />
        {streak >= 3 && (
          <div ref={streakRef} data-testid="tri-streak" style={{
            display: 'flex', alignItems: 'center', gap: '0.25rem',
            padding: '0.15rem 0.6rem', borderRadius: '0.5rem',
            background: 'rgba(245,159,0,0.12)', color: '#F59F00',
            fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 700,
          }}>
            <Zap size={16} fill="#F59F00" /> {streak}
          </div>
        )}
        <div style={{ fontFamily: 'Fredoka', fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600 }}>
          Set {setIdx + 1}/{maxSets}
        </div>
      </GameHeader>

      {/* ─── Game Area ─── */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 'clamp(1rem, 2.5vh, 1.75rem)', padding: '1rem',
      }}>
        {/* Set title */}
        <div style={{
          fontFamily: 'Fredoka', fontSize: 'clamp(1rem, 2vw, 1.3rem)', fontWeight: 700,
          color: 'var(--text-secondary)', textAlign: 'center',
        }}>
          {currentSet.title || currentSet.title_en}
        </div>

        {/* Word card */}
        <div ref={wordRef} data-testid="tri-current-word" style={{
          padding: 'clamp(1rem, 2.5vh, 1.5rem) clamp(3rem, 8vw, 6rem)',
          borderRadius: '1.5rem',
          background: 'rgba(255,255,255,0.95)',
          border: feedback === 'correct' ? '3px solid var(--success)' : feedback === 'wrong' ? '3px solid var(--error)' : '2px solid rgba(76,110,245,0.12)',
          fontSize: 'clamp(2.5rem, 5.5vw, 4rem)', fontWeight: 700, fontFamily: 'Fredoka',
          color: 'var(--text-primary)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
          textAlign: 'center',
          transition: 'border-color 0.15s',
        }}>
          {currentWord.display || currentWord.text}
        </div>

        {/* Word counter */}
        <div style={{
          fontSize: 'clamp(0.85rem, 1.3vw, 1rem)', color: 'var(--text-muted)', fontWeight: 600,
          fontFamily: 'Nunito',
        }}>
          {wordIdx + 1} of {currentWords.length}
        </div>

        {/* Wrong answer hint */}
        {feedback === 'wrong' && correctCategory !== null && (
          <div data-testid="tri-correct-hint" style={{
            padding: '0.35rem 1rem', borderRadius: '0.6rem',
            background: 'rgba(224,49,49,0.06)', border: '2px solid rgba(224,49,49,0.15)',
            fontFamily: 'Fredoka', fontSize: 'clamp(0.9rem, 1.5vw, 1.1rem)', fontWeight: 700,
            color: 'var(--error)',
          }}>
            {categories.find(c => c.index === correctCategory)?.label || '?'}
          </div>
        )}

        {/* Category buttons */}
        <div style={{
          display: 'flex', gap: 'clamp(0.75rem, 1.5vw, 1.25rem)',
          flexWrap: catCount > 3 ? 'wrap' : 'nowrap',
          justifyContent: 'center', width: '100%',
          maxWidth: catCount <= 2 ? 650 : catCount === 3 ? 850 : 900,
        }}>
          {categories.map((cat, idx) => {
            const c = CAT_COLORS[idx % CAT_COLORS.length];
            const count = sortedCount(cat.index);
            const isCorrectTarget = feedback === 'wrong' && correctCategory === cat.index;
            const totalForCat = currentWords.filter(w => w.category === cat.index).length;

            return (
              <button key={idx} data-testid={`tri-cat-${idx}`}
                onClick={(e) => handleSort(cat.index, e.currentTarget)}
                disabled={!!feedback}
                style={{
                  flex: catCount <= 3 ? '1 1 0' : '1 1 calc(50% - 0.75rem)',
                  minHeight: 'clamp(80px, 12vh, 110px)',
                  padding: 'clamp(0.75rem, 1.5vh, 1rem) clamp(1rem, 2vw, 1.5rem)',
                  borderRadius: '1.25rem',
                  background: isCorrectTarget ? '#2F9E44' : c.bg,
                  color: 'white',
                  border: 'none',
                  borderBottom: `6px solid ${isCorrectTarget ? '#2B8A3E' : c.border}`,
                  cursor: feedback ? 'default' : 'pointer',
                  touchAction: 'manipulation',
                  boxShadow: isCorrectTarget
                    ? '0 0 0 4px rgba(47,158,68,0.3), 0 4px 16px rgba(47,158,68,0.2)'
                    : `0 4px 16px ${c.glow}`,
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: '0.25rem',
                  transition: 'transform 0.08s, background 0.15s, box-shadow 0.15s',
                }}
                onMouseDown={e => { if (!feedback) e.currentTarget.style.transform = 'translateY(4px)'; }}
                onMouseUp={e => e.currentTarget.style.transform = ''}
              >
                <span style={{
                  fontFamily: 'Fredoka', fontWeight: 700,
                  fontSize: 'clamp(1.2rem, 2.2vw, 1.7rem)',
                  lineHeight: 1.2, textAlign: 'center',
                }}>{cat.label}</span>
                <span style={{
                  fontFamily: 'Fredoka', fontWeight: 700, opacity: 0.75,
                  fontSize: 'clamp(0.85rem, 1.3vw, 1.05rem)',
                }}>{count} / {totalForCat}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
